import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import _, { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';

import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { Button } from '../../../components/ui/Button';

import { useUpdateCampaignContentSettings, useUpdateMedia, useUpdatePlaylistContentSettings } from '../../../queries';
import { handleRssSetting, trimFileNameExc } from '../../../config/global';
import { readImageFit } from '../../../utils/imageFit';
import { TYPES_ALLOWED_STRETCH } from '../../../Constants/media';
import { setRssSettings } from '../../../redux/Slices/Apps/appsSlice';
import { toggleDialog } from '../../../redux/Slices/Global/globalSlice';
import RssSettings from '../RSS/RssSettings';
import { Media } from '../ShowMedia/Media';
import CornerMediaPreview from './components/CornerMediaPreview';
import CornerAreaControl from './components/CornerAreaControl';

const defaultArea = {
  start_margin: 0,
  top_margin: 0,
  width: 35,
  height: 37,
  layout: "landscape-wide"
};


const rssTypes = ["RSS", "RSS_CUSTOM"]

function MediaSettingPopup({ item, removeMedia, setSelectedRow, onPublish }) {
  const { t } = useTranslation();
  const { formState: { errors }, control, reset } = useForm();
  const { layer, openDialog } = useSelector((state) => state.global);

  const { rssSettings } = useSelector(state => state.apps);
  const dispatch = useDispatch();

  const updateCampaignItemSetting = useUpdateCampaignContentSettings();
  const updatePlaylistItemSetting = useUpdatePlaylistContentSettings();
  const updateMediaName = useUpdateMedia();
  const loading = useMemo(() => updateCampaignItemSetting?.isLoading || updatePlaylistItemSetting.isLoading,
    [updateCampaignItemSetting?.isLoading, updatePlaylistItemSetting.isLoading])

  const [editName, setEditName] = useState(false)
  const [name, setName] = useState(trimFileNameExc(item.file_name))


  const [setting, setSetting] = useState({
    "show_video_screen": item.show_video_screen,
    "is_muted": item.is_muted,
    "maintain_aspect_ratio": item.maintain_aspect_ratio,
    "image_fit": readImageFit(item),
    "settings": item.media_settings,
    "is_showed": item.is_showed,
    "downloadable": item.downloadable,
    "show_layer_two": item.show_layer_two,
    "show_layer_one": item.show_layer_one,
    "show_layer_three": item.show_layer_three,
  });


  const [cornerMediaSettings, setCornerMediaSettings] = useState(item.media_settings)

  useEffect(() => {
    let _settings = handleRssSetting(item?.rss_settings, item?.title);
    dispatch(setRssSettings(_settings))
    reset(_settings)
  }, [])

  // Sync setting state when item prop changes (e.g. from global update)
  useEffect(() => {
      setSetting({
        "show_video_screen": item.show_video_screen,
        "is_muted": item.is_muted,
        "maintain_aspect_ratio": item.maintain_aspect_ratio,
        "image_fit": readImageFit(item),
        "settings": item.media_settings,
        "is_showed": item.is_showed,
        "downloadable": item.downloadable,
        "show_layer_two": item.show_layer_two,
        "show_layer_one": item.show_layer_one,
        "show_layer_three": item.show_layer_three,
      });
      setCornerMediaSettings(item.media_settings);
      setName(trimFileNameExc(item.file_name));
  }, [item]);

  const updateSetting = async () => {
    try {
      const formData = new FormData();

      for (var key in setting) {
        if (key === "settings") {
          const orderedSettings = {
            width: cornerMediaSettings?.width || 100,
            height: cornerMediaSettings?.height || 100,
            top_margin: cornerMediaSettings?.top_margin || 0,
            start_margin: cornerMediaSettings?.start_margin || 0,
          };
          formData.append("settings", JSON.stringify(orderedSettings));
        } else if (key === "image_fit") {
          // ⚠️ NOT Number(). Every other key here is a boolean flag and the loop
          // coerces with Number(); image_fit is a string, and Number('crop') is
          // NaN — it would post "NaN" and fail the in:fit,crop,fill rule.
          if (setting.image_fit) formData.append("image_fit", setting.image_fit);
        } else {
          formData.append(key, Number(setting[key]));
        }
      }

      if (rssTypes?.includes(item.type)) {
        updateRssSettings(formData);
      }

      if (item.playlist_media_id || window.location.pathname.includes("playlist")) {
        formData.append("playlist_media_id", item.playlist_media_id);

        await updatePlaylistItemSetting.mutateAsync(formData);
        setSelectedRow({});

      } else {
        await updateCampaignItemSetting.mutateAsync({
          data: formData,
          id: item.campaign_playlist_media_id
        });
        setSelectedRow({});
      }
      if (onPublish) onPublish();
    } catch (error) {
      console.error("Error updating settings:", error);
    }
  };


  const updateRssSettings = (formData) => {
    let rss_settings = {
      ...rssSettings,
      direction: rssSettings?.direction === "left" ? "ltr" : "rtl",
      font_size: Number(rssSettings?.font_size),
    };

    for (var key in rss_settings) {
      if (key !== "newSeparator" || !isEmpty(rssSettings?.newSeparator)) {
        formData.append(`rss_settings[${key}]`, rss_settings[key]);
      }
    }

  }

  const updateName = async () => {
    if (item.file_name !== name) {
      await updateMediaName.mutateAsync({
        id: item.media_id,
        data: { "name": `${name}.xml` }
      }, {
        onSuccess: (data) => {
          setEditName(false)
          item.file_name = trimFileNameExc(data.file_name)
        }
      })
    } else {
      setEditName(false)
    }
  }

  //Dialog Header 
  const header = () => {
    return editName ?
      <div className='d-flex align-items-center ' >
        <InputText className='w-50' value={name} onChange={(e) => setName(e.target.value)} />
        <div className="flex  ">
          <Button label="" icon="fa-solid fa-check"
            loading={updateMediaName.isLoading}
            className="text-xl py-0 mx-2 bg-transparent text-black border-transparent "
            type="submit"
            onClick={updateName}
          />
        </div>
      </div>
      :
      <h4 className=' m-0 fw-bold d-flex align-items-center '>
        <span>{name.length > 20 ? name.slice(0, 20) + "..." : name}</span>
        <Button label="" icon="fas fa-pencil" className="text-lg py-0 mx-2 bg-transparent text-black border-transparent " type="button"
          onClick={() => setEditName(true)} />
      </h4 >
  }

  const onclose = () => {
    dispatch(toggleDialog("mediaSettingPopup"))
    setSelectedRow({})
  }

  const getClass = (item, layer) => {
    if (
      (layer === "CORNER" || layer === "PLAYGROUND") &&
      (item.type === "IMAGE" || item.type === "CORNER")
    ) {
      return "w-[45%]";
    } else {
      return "col-6 col-lg-5";
    }
  };



  return (
    <>
      <Dialog visible={openDialog.mediaSettingPopup}
        onHide={onclose} draggable={false}
        breakpoints={{ '960px': '95vw' }}


        style={{
          width: "70vw",
          height: item.type === "WEATHER" || item.type === "CLOCK" ? "" : "80vh",
        }}
        header={header} >
        <div className="flex flex-column  flex-md-row h-100">
          {(layer === "CORNER" || layer === "PLAYGROUND") && (item.type === "IMAGE" || item.type === "CORNER") ?
            <div className="media-container-settings col-12 col-md-7 d-flex flex-column flex-md-row h-100  justify-content-center bg-gray-100">
              <CornerMediaPreview settings={true} imageFit={setting.image_fit} maintainAspectRatio={setting.maintain_aspect_ratio} item={item} area={cornerMediaSettings} setArea={setCornerMediaSettings} />
            </div>
            :
            <div className="col-12 col-md-7 d-flex flex-column flex-md-row h-100  justify-content-center bg-gray-100">
              <div className={`col-12  p-3 h-100 flex justify-center items-center
              ${item?.rss_settings?.position == "BOTTOM" ? "justify-content-end border-2 border-dark" : ""}  `}
                style={{ minHeight: "300px", maxHeight: "628px" }}>

                <Media data={item} imageFit={setting.image_fit} stretch={!setting.maintain_aspect_ratio} />
              </div>
            </div>
          }

          <div className={`${getClass(item, layer)} col-12 col-md-4 px-0 d-flex border-l flex-column justify-content-between overflow-y-auto`}>
            {
              rssTypes?.includes(item.type) ?
                <div className="px-2 mt-3 ">
                  <h6 className='text-center text-black fw-bold mb-2'>{t('dragdrop.rss_settings')}</h6>
                  <RssSettings errors={errors} control={control} reset={reset} />
                </div>
                :
                <div className="px-2 mt-3">
                  <h6 className='text-center text-black fw-bold'>{t('dragdrop.media_settings')}</h6>

                  {
                    item.section != "LINKS" &&
                    <div className="d-flex justify-content-between mb-2">
                      <span className='fs-8'>{t('dragdrop.downloadable')}</span>
                      <InputSwitch checked={setting.downloadable} onChange={(e) => setSetting({ ...setting, "downloadable": e.value })} />
                    </div>
                  }

                  <div className="d-flex justify-content-between mb-2">
                    <span className='fs-8'>{t('dragdrop.mute')}</span>
                    <InputSwitch checked={setting.is_muted} onChange={(e) => setSetting({ ...setting, "is_muted": e.value })} />
                  </div>
                  {/* ⚠️ REPLACES THE "fill to screen" TOGGLE. That toggle had two
                      states and neither is what a café wants: on maps to FIT_XY,
                      a NON-UNIFORM scale that stretches and distorts, and off
                      letterboxes. "Fill the screen" has always meant "ruin the
                      image". crop scales up uniformly and trims the overflow.
                      The old boolean is still sent so an older player, which
                      knows nothing about image_fit, keeps behaving as today. */}
                  <div className="mb-2">
                    <span className='fs-8 d-block mb-1'>{t('dragdrop.image_fit.label')}</span>
                    <div className="d-flex gap-1 flex-wrap">
                      {['fit', 'crop', 'fill'].map((mode) => {
                        const active = (setting.image_fit || (setting.maintain_aspect_ratio ? 'fit' : 'fill')) === mode;
                        return (
                          <button
                            key={mode} type="button"
                            disabled={!(TYPES_ALLOWED_STRETCH.includes(item.type))}
                            onClick={() => setSetting((prev) => ({
                              ...prev,
                              image_fit: mode,
                              // Keep the legacy boolean truthful for older players:
                              // crop and fit both preserve the aspect ratio.
                              maintain_aspect_ratio: mode !== 'fill',
                            }))}
                            className={`btn btn-sm ${active ? 'btn-primary' : 'btn-outline-secondary'}`}
                            style={{ fontSize: '0.72rem', flex: '1 1 0', minWidth: 0 }}
                            title={t(`dragdrop.image_fit.${mode}_hint`)}
                          >
                            {t(`dragdrop.image_fit.${mode}`)}
                          </button>
                        );
                      })}
                    </div>
                    <small className="text-muted d-block mt-1" style={{ fontSize: '0.68rem' }}>
                      {t(`dragdrop.image_fit.${setting.image_fit || (setting.maintain_aspect_ratio ? 'fit' : 'fill')}_hint`)}
                    </small>
                  </div>

                  {(layer === "CORNER" || layer === "PLAYGROUND") && (item.type === "IMAGE" || item.type === "CORNER") ? (
                    <div className="px-2 border-top py-3" >
                      <h6 className='text-center text-black fw-bold'>{t('dragdrop.corner_settings')}</h6>
                      <CornerAreaControl layer={true} area={cornerMediaSettings} setArea={setCornerMediaSettings} />
                    </div>
                  ) : ""}
                </div>
            }

            {
              layer == "MAIN" ? window.location.pathname.includes("campaign") ?
                <div className="px-2 border-top py-3" >
                  <h6 className='text-center text-black fw-bold'>{t('dragdrop.play_settings')}</h6>

                  <div className="d-flex justify-content-between mb-2">
                    <span className='fs-8'>{t('dragdrop.show_playground_layer')}</span>
                    <InputSwitch checked={setting.show_layer_one} onChange={(e) => setSetting({ ...setting, "show_layer_one": e.value })} />
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span className='fs-8'>{t('dragdrop.show_corner_layer')}</span>
                    <InputSwitch checked={setting.show_layer_two} onChange={(e) => setSetting({ ...setting, "show_layer_two": e.value })} />
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span className='fs-8'>{t('dragdrop.show_banner_layer')}</span>
                    <InputSwitch inputId=' `${ item.id }-layer-three` }' checked={setting.show_layer_three} onChange={(e) => setSetting({ ...setting, "show_layer_three": e.value })} />
                  </div>
                </div> :
                ""
                :
                ""
            }
            <div className='border-top px-2 py-3 d-flex justify-content-between align-items-center'>
              <div className='d-flex justify-content-center align-items-center'>
                {
                  item.downloadable == 1 ?
                    <a href={item.path} >
                      <i className="fas fa-download me-2 cursor-pointer text-light fs-5" ></i>
                    </a>
                    :
                    ""
                }
                <i className="bx bx-trash text-light cursor-pointer fs-5" onClick={removeMedia} ></i>
              </div>
              <Button label={["RSS", "RSS_CUSTOM"].includes(item.type) ? t('buttons.publish') : t('buttons.update')}
                loading={loading}
                className='btn btn-primary fs-8 py-1 px-2'
                onClick={updateSetting} />
            </div>
          </div>
        </div>
      </Dialog >
    </>
  )
}

export default MediaSettingPopup